import { FORNECEDOR_MSG_CADASTRO_INATIVO_PADRAO } from './fornecedorAuthMessages';

/**
 * Lista de emails de fornecedores aprovados.
 * Compartilhado entre admin e guia quando no mesmo domínio (localStorage).
 * Admin adiciona ao aprovar; guia verifica no login.
 */

const KEY_APROVADOS = 'naviex-fornecedores-aprovados';
const KEY_PENDENTES = 'naviex-fornecedores-pendentes';
const KEY_RECUSADOS = 'naviex-fornecedores-recusados';
const KEY_INATIVOS = 'naviex-fornecedores-inativos';
const KEY_MOTIVO_INATIVO = 'naviex-fornecedor-inativo-motivo';

function loadSet(key: string): Set<string> {
  try {
    const s = localStorage.getItem(key);
    if (!s) return new Set();
    const arr = JSON.parse(s) as string[];
    return new Set((arr || []).map((e) => (e || '').trim().toLowerCase()).filter(Boolean));
  } catch {
    return new Set();
  }
}

function saveSet(key: string, set: Set<string>) {
  try {
    localStorage.setItem(key, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

export function isAprovado(email: string): boolean {
  return loadSet(KEY_APROVADOS).has((email || '').trim().toLowerCase());
}

export function addAprovado(email: string): void {
  const e = (email || '').trim().toLowerCase();
  const set = loadSet(KEY_APROVADOS);
  set.add(e);
  saveSet(KEY_APROVADOS, set);
  const rec = loadSet(KEY_RECUSADOS);
  rec.delete(e);
  saveSet(KEY_RECUSADOS, rec);
}

/** Remove da lista de aprovados (ex.: inativo ou antes de nova análise). */
export function removeAprovado(email: string): void {
  const set = loadSet(KEY_APROVADOS);
  set.delete((email || '').trim().toLowerCase());
  saveSet(KEY_APROVADOS, set);
}

export function isRecusado(email: string): boolean {
  return loadSet(KEY_RECUSADOS).has((email || '').trim().toLowerCase());
}

export function addRecusado(email: string): void {
  const e = (email || '').trim().toLowerCase();
  const set = loadSet(KEY_RECUSADOS);
  set.add(e);
  saveSet(KEY_RECUSADOS, set);
  const ap = loadSet(KEY_APROVADOS);
  ap.delete(e);
  saveSet(KEY_APROVADOS, ap);
}

export function removeRecusado(email: string): void {
  const set = loadSet(KEY_RECUSADOS);
  set.delete((email || '').trim().toLowerCase());
  saveSet(KEY_RECUSADOS, set);
}

export function isPendente(email: string): boolean {
  return loadSet(KEY_PENDENTES).has((email || '').trim().toLowerCase());
}

export function addPendente(email: string): void {
  const set = loadSet(KEY_PENDENTES);
  set.add((email || '').trim().toLowerCase());
  saveSet(KEY_PENDENTES, set);
}

export function removePendente(email: string): void {
  const set = loadSet(KEY_PENDENTES);
  set.delete((email || '').trim().toLowerCase());
  saveSet(KEY_PENDENTES, set);
}

export function getAprovados(): string[] {
  return [...loadSet(KEY_APROVADOS)];
}

export function isInativo(email: string): boolean {
  return loadSet(KEY_INATIVOS).has((email || '').trim().toLowerCase());
}

export function addInativo(email: string): void {
  const e = (email || '').trim().toLowerCase();
  if (!e) return;
  const set = loadSet(KEY_INATIVOS);
  set.add(e);
  saveSet(KEY_INATIVOS, set);
}

export function removeInativo(email: string): void {
  const set = loadSet(KEY_INATIVOS);
  set.delete((email || '').trim().toLowerCase());
  saveSet(KEY_INATIVOS, set);
}

function loadMotivoInativoMap(): Record<string, string> {
  try {
    const s = localStorage.getItem(KEY_MOTIVO_INATIVO);
    return s ? (JSON.parse(s) as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function saveMotivoInativoMap(map: Record<string, string>) {
  try {
    localStorage.setItem(KEY_MOTIVO_INATIVO, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function getMotivoInativoParceiro(email: string): string | null {
  const e = (email || '').trim().toLowerCase();
  const m = loadMotivoInativoMap()[e];
  return m != null && String(m).trim() !== '' ? String(m).trim() : null;
}

export function setMotivoInativoParceiro(email: string, motivo: string): void {
  const e = (email || '').trim().toLowerCase();
  if (!e) return;
  const map = loadMotivoInativoMap();
  map[e] = motivo.trim();
  saveMotivoInativoMap(map);
}

export function clearMotivoInativoParceiro(email: string): void {
  const e = (email || '').trim().toLowerCase();
  const map = loadMotivoInativoMap();
  delete map[e];
  saveMotivoInativoMap(map);
}

function normalizeCadastroStatusApi(raw: string): string {
  const s = (raw || '').trim().toLowerCase();
  if (s === 'ativo' || s === 'active' || s === 'approved') return 'aprovado';
  if (s === 'em_analise' || s === 'em análise' || s === 'analise') return 'pendente';
  return s;
}

/**
 * Alinha localStorage com o status vindo da API (GET /api/parceiros/cadastro-status).
 * Usado pelo app do guia para liberar ou bloquear acesso quando o admin aprovar/reprovar na nuvem.
 */
export function aplicarCadastroStatusParceiroApi(
  email: string,
  cadastroStatus: string,
  motivoInativo?: string | null
): void {
  const e = (email || '').trim().toLowerCase();
  if (!e) return;
  const s = normalizeCadastroStatusApi(cadastroStatus);
  if (s === 'aprovado') {
    removeInativo(e);
    clearMotivoInativoParceiro(e);
    addAprovado(e);
    removePendente(e);
    return;
  }
  if (s === 'recusado') {
    removeInativo(e);
    clearMotivoInativoParceiro(e);
    addRecusado(e);
    removePendente(e);
    return;
  }
  if (s === 'inativo') {
    removeAprovado(e);
    removePendente(e);
    addInativo(e);
    const m =
      motivoInativo != null && String(motivoInativo).trim() !== ''
        ? String(motivoInativo).trim()
        : FORNECEDOR_MSG_CADASTRO_INATIVO_PADRAO;
    setMotivoInativoParceiro(e, m);
    return;
  }
  if (s === 'pendente') {
    removeInativo(e);
    clearMotivoInativoParceiro(e);
    removeAprovado(e);
    addPendente(e);
    return;
  }
  removeAprovado(e);
  removeInativo(e);
  clearMotivoInativoParceiro(e);
}
